import { useState } from 'react';
import { Link } from 'react-router-dom';
import { InstagramLogo, FacebookLogo, TwitterLogo, EnvelopeSimple, ArrowRight } from '@phosphor-icons/react';
import api from '../lib/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';

function FooterSignup() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post('/subscribe', { email: email.trim(), source: 'footer' });
      setDone(true);
      if (r.data?.already) toast.info("You're already on the list!");
      else if (r.data?.resubscribed) toast.success("Welcome back — you're resubscribed.");
      else toast.success("You're on the list ✉️");
      // Mark globally so popup never re-shows
      try { localStorage.setItem('pwf_newsletter_state', 'subscribed'); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not subscribe — try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <p className="text-sm text-turquoise" data-testid="footer-subscribe-success">
        ✓ Thanks — you'll get the next pow wow digest in your inbox.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="flex gap-2 max-w-sm" data-testid="footer-subscribe-form">
      <div className="relative flex-1">
        <EnvelopeSimple size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="pl-9 h-10 bg-navy/40"
          data-testid="footer-subscribe-input"
        />
      </div>
      <Button
        type="submit"
        disabled={submitting}
        size="sm"
        className="bg-turquoise text-navy hover:bg-turquoise/90 rounded-full h-10 px-4 font-semibold whitespace-nowrap"
        data-testid="footer-subscribe-btn"
      >
        {submitting ? '…' : <>Join <ArrowRight size={14} weight="bold" className="ml-1" /></>}
      </Button>
    </form>
  );
}

export default function Footer() {
  return (
    <footer className="bg-card border-t border-border mt-24 relative overflow-hidden">
      <div className="absolute inset-0 pattern-aztec opacity-40" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img
              src="/icon-192.png"
              alt="Pow Wow Finder"
              className="h-12 w-12 rounded-full ring-2 ring-turquoise/40 object-cover"
            />
            <div>
              <p className="font-display text-2xl">Pow Wow <span className="text-turquoise">Finder</span></p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Find · Plan · Celebrate · Honor</p>
            </div>
          </div>
          <p className="text-muted-foreground max-w-md leading-relaxed mb-6">
            Discover Native American and First Nations pow wows and cultural events across Turtle Island.
            Built with respect, in partnership with host nations and dancers.
          </p>
          <div>
            <h4 className="label text-gold mb-2">Pow wow digest</h4>
            <p className="text-xs text-muted-foreground mb-3 max-w-sm">Free weekly email — new events, drum contests, big payouts. Once a week, never spam.</p>
            <FooterSignup />
          </div>
        </div>
        <div>
          <h4 className="label text-gold mb-4">Explore</h4>
          <ul className="space-y-2 text-foreground/80">
            <li><Link to="/events" className="hover:text-turquoise">All Events</Link></li>
            <li><Link to="/map" className="hover:text-turquoise">Map View</Link></li>
            <li><Link to="/plan" className="hover:text-turquoise">Trip Planner</Link></li>
            <li><Link to="/submit" className="hover:text-turquoise">Submit Pow Wow</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="label text-gold mb-4">Community</h4>
          <ul className="space-y-2 text-foreground/80">
            <li><Link to="/submit" className="hover:text-turquoise">For Organizers</Link></li>
            <li><Link to="/plan" className="hover:text-turquoise">Plan a Trip</Link></li>
            <li><a href="mailto:n8vcouture@gmail.com" className="hover:text-turquoise">Contact us</a></li>
          </ul>
        </div>
      </div>
      <div className="relative border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© 2026 Pow Wow Finder · Your journey. Our culture. One community.</p>
          <div className="flex items-center gap-3">
            <a href="#" aria-label="Instagram" className="hover:text-turquoise"><InstagramLogo size={20} /></a>
            <a href="#" aria-label="Facebook" className="hover:text-turquoise"><FacebookLogo size={20} /></a>
            <a href="#" aria-label="X" className="hover:text-turquoise"><TwitterLogo size={20} /></a>
          </div>
        </div>
      </div>
    </footer>
  );
}
